import { useEffect, useRef, useState } from "react";
import { RotateCcw, Eye, X } from "lucide-react";

/**
 * Lightweight signature pad using HTMLCanvas with mouse/touch.
 */
export default function SignaturePad({ value, onChange, testid = "signature-pad" }) {
  const canvasRef = useRef(null);
  const drawingRef = useRef(false);
  const lastRef = useRef({ x: 0, y: 0 });
  const [hasSignature, setHasSignature] = useState(!!value);
  const [preview, setPreview] = useState(false);

  // Restore from value
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    const ctx = canvas.getContext("2d");
    ctx.scale(dpr, dpr);
    ctx.lineCap = "round";
    ctx.strokeStyle = "#062B2B";
    ctx.lineWidth = 2.2;
    ctx.fillStyle = "#fff";
    ctx.fillRect(0, 0, rect.width, rect.height);
    if (value) {
      const img = new Image();
      img.onload = () => ctx.drawImage(img, 0, 0, rect.width, rect.height);
      img.src = value;
      setHasSignature(true);
    }
  }, []); // eslint-disable-line

  const getPos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const point = e.touches ? e.touches[0] : e;
    return { x: point.clientX - rect.left, y: point.clientY - rect.top };
  };

  const start = (e) => {
    e.preventDefault();
    drawingRef.current = true;
    lastRef.current = getPos(e);
  };
  const move = (e) => {
    if (!drawingRef.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    const pos = getPos(e);
    ctx.beginPath();
    ctx.moveTo(lastRef.current.x, lastRef.current.y);
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
    lastRef.current = pos;
  };
  const end = () => {
    if (!drawingRef.current) return;
    drawingRef.current = false;
    setHasSignature(true);
    onChange?.(canvasRef.current.toDataURL("image/png"));
  };

  const clear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const rect = canvas.getBoundingClientRect();
    ctx.fillStyle = "#fff";
    ctx.fillRect(0, 0, rect.width, rect.height);
    setHasSignature(false);
    onChange?.(null);
  };

  return (
    <div data-testid={testid}>
      <div className="rounded-2xl border border-brand/15 bg-white overflow-hidden">
        <canvas
          ref={canvasRef}
          className="sig-canvas"
          onMouseDown={start}
          onMouseMove={move}
          onMouseUp={end}
          onMouseLeave={end}
          onTouchStart={start}
          onTouchMove={move}
          onTouchEnd={end}
        />
      </div>
      <div className="mt-3 flex items-center justify-between">
        <div className={"text-sm flex items-center gap-2 " + (hasSignature ? "text-emerald-600" : "text-brand-muted")}>
          <span className={"w-2 h-2 rounded-full " + (hasSignature ? "bg-emerald-500" : "bg-brand-muted/40")} />
          {hasSignature ? "Tanda tangan tersimpan" : "Belum ada tanda tangan"}
        </div>
        <div className="flex gap-2">
          {hasSignature && (
            <button
              type="button"
              onClick={() => setPreview(true)}
              data-testid="signature-preview-btn"
              className="text-sm rounded-full px-3 py-1.5 bg-brand-soft text-brand flex items-center gap-1.5"
            >
              <Eye size={14} /> Pratinjau
            </button>
          )}
          <button
            type="button"
            onClick={clear}
            data-testid="signature-clear-btn"
            className="text-sm rounded-full px-3 py-1.5 border border-amber-300 text-amber-700 flex items-center gap-1.5"
          >
            <RotateCcw size={14} /> Ulangi
          </button>
        </div>
      </div>

      {preview && (
        <div className="fixed inset-0 z-50 bg-brand-ink/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setPreview(false)}>
          <div className="bg-white rounded-2xl p-4 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <div className="font-heading font-bold text-brand-ink">Pratinjau Tanda Tangan</div>
              <button onClick={() => setPreview(false)} className="p-2 rounded-full hover:bg-brand-soft"><X size={16} /></button>
            </div>
            <img src={value || canvasRef.current?.toDataURL()} alt="signature" className="w-full bg-white rounded-xl border border-brand/10" />
          </div>
        </div>
      )}
    </div>
  );
}
